import { PulseEventEnvelope } from './types';

// Deliberately plain `fetch` — NOT the configured/auth-intercepted matchingApi instance:
// analytics must never trigger token refresh, 401 redirects, localStorage.clear(), or
// reloads. (This is the intentional exception to "use the configured axios instance".)
const PULSE_PATH = '/pulse/batch';

const pulseUrl = (): string => `${import.meta.env.VITE_API_BASE_URL}${PULSE_PATH}`;

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export const sendPulseBatch = async (
  events: PulseEventEnvelope[],
  opts: { beacon?: boolean } = {},
): Promise<void> => {
  if (events.length === 0) return;
  const body = JSON.stringify({ events });

  // Unload path: prefer sendBeacon, but it returns false if the payload exceeds the
  // browser queue limit — fall through to a keepalive fetch rather than silently drop.
  if (opts.beacon) {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const queued = navigator.sendBeacon(
        pulseUrl(),
        new Blob([body], { type: 'application/json' }),
      );
      if (queued) return;
    }
    await fetch(pulseUrl(), { method: 'POST', body, headers: JSON_HEADERS, keepalive: true });
    return;
  }

  // keepalive so a flush already in flight when the page unloads isn't cancelled — the
  // beacon path covers what's still queued, this covers the batch already on the wire.
  // Batches are bounded (MAX_BATCH events), well under the 64KB keepalive body cap.
  const response = await fetch(pulseUrl(), {
    method: 'POST',
    body,
    headers: JSON_HEADERS,
    keepalive: true,
  });
  if (!response.ok) {
    // Shape the error like axios so the client's retry logic can read the status.
    throw Object.assign(new Error(`pulse ${response.status}`), {
      response: { status: response.status },
    });
  }
};
